import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CalendarCheck, MessageCircle, ChevronDown } from "lucide-react";
import { Reveal, Overline, MaskedHeading } from "@/components/ui-bits";
import { submitContact, NEED_OPTIONS, BOOKING_URL, WHATSAPP_URL } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", need: "", message: "" };

export default function ContactCTA() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.need || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Received. Foresight will be in touch shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/25 focus:border-gold text-white rounded-none px-0 py-3 outline-none transition-colors placeholder:text-white/35";
  const labelCls = "text-xs uppercase tracking-[0.2em] text-gold";

  return (
    <section
      id="contact"
      className="relative bg-navy-deep grain py-24 sm:py-32 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Left: Final CTA */}
          <div>
            <Reveal>
              <Overline>Get Started</Overline>
            </Reveal>
            <MaskedHeading
              tag="h2"
              className="mt-6 font-display font-bold text-white leading-[1.04] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
              lines={["Verify Before You Trust.", "Verify Before You Invest."]}
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-white/70 leading-relaxed text-lg">
                Free consultation. No obligation. Just clarity.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-book-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-gold text-navy-deep hover:bg-gold-light transition-colors duration-300 font-semibold px-8 py-4 text-sm uppercase tracking-wider"
                >
                  <CalendarCheck size={18} />
                  Book Your Free Consultation
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-whatsapp-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-gold hover:text-gold transition-colors duration-300 font-semibold px-8 py-4 text-sm uppercase tracking-wider"
                >
                  <MessageCircle size={18} />
                  Message us on WhatsApp
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-12 font-display italic text-white/40 text-xl">
                “I Investigate. You Decide.”
              </p>
            </Reveal>
          </div>

          {/* Right: Contact form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              className="bg-navy/50 border border-white/10 p-8 sm:p-10 space-y-6"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                    placeholder="you@email.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-phone-input"
                  placeholder="+1 / +44 / +233 …"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>What do you need help with? *</label>
                <div className="relative">
                  <select
                    value={form.need}
                    onChange={(e) => setForm({ ...form, need: e.target.value })}
                    data-testid="contact-need-select"
                    className={`${inputCls} appearance-none cursor-pointer pr-8 ${
                      form.need ? "text-white" : "text-white/35"
                    }`}
                  >
                    <option value="" disabled className="text-navy-deep">
                      Select an option
                    </option>
                    {NEED_OPTIONS.map((o) => (
                      <option key={o} value={o} className="text-navy-deep">
                        {o}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="text-gold absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Message *</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message-input"
                  placeholder="Tell us briefly what you're considering…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={loading}
                data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy-deep hover:bg-gold-light transition-colors duration-300 font-semibold px-6 py-4 text-sm uppercase tracking-wider disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                Send Message
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
